import {
  Component, ElementRef, forwardRef,
  Input, OnInit, Optional, Renderer2, Self, ViewChild
} from '@angular/core';

import {ControlValueAccessor, FormBuilder, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import {BaseControlValueAccessor} from '../util/base-control-value-accessor';
import {stringify} from 'querystring';
import {Key} from '../util/key';

const CLO_INPUT_VALID_TYPES = [
  'email',
  'password',
  'tel',
  'text',
  'url',
  'search'
];

const CLO_INPUT_VALID_DIRECTIVES = [
  'currency'
];

/**
 * Use text fields in forms to help people enter, select, and search for text. Text fields are normally found within a form but can also
 * be part of a modal, search, or card. Common text input types include: usernames, descriptions, URLs, emails, addresses,
 * and plain text searches.
 */
@Component({
  selector: 'clo-input-text',
  templateUrl: './text-input.html',
  styleUrls: ['./input.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CloInputText),
      multi: true
    }
  ]

})
export class CloInputText implements OnInit, ControlValueAccessor {
  /**
   * The input type, defaults to 'text'
   * The following types are supported:   `'email'`, `'password'`,
   `'tel'`, `'text'`, `'url', 'search'`
   */
  @Input()
  get type(): string { return this._type; }
  set type(value: string) {
    this._type = value || 'text';
    if (value) {
      this._validateType();
    }
  }

  protected _type = 'text';

  /**
   * A directive, defaults to ''
   * The following types are supported:   `'currency'`
   */
  @Input()
  get directive(): string { return this._directive; }
  set directive(value: string) {
    this._directive = value || '';
    if (value) {
      this._validateDirective();
    }
  }
  protected _directive = '';

  /**
   * Boolean value for disabling the field
   */
  @Input() disabled: boolean;

  /**
   * Placeholder Text
   */
  @Input() placeholder: string;

  /**
   * Error Message
   */
  @Input() errorMessage = '';

  /**
   * Boolean for whether the field is required
   *
   */
  @Input() required: boolean;

  /**
   * Boolean for whether the field is readOnly
   *
   */
  @Input() readOnly: boolean;

  @Input() name: string;
  // /**
  //  * Value entered into the text field
  //  *
  //  */
  @Input('value')
  val: string;
  @ViewChild('input', {static: false}) el: ElementRef;
  // @ViewChild('input', {static: false}) input;

  invalid: boolean;
  valid: boolean;
  control: FormControl;

  get value() {
    return this.val;
  }

  set value(val) {
    this.val = val;
    this.onChange();
    this.onTouchedFn();
  }

  protected _validateType() {
    if (CLO_INPUT_VALID_TYPES.indexOf(this._type) === -1) {
      throw Error(`Input type "${this._type}" isn't supported by CloInputText.`);
    }
  }

  protected _validateDirective() {
    if (CLO_INPUT_VALID_DIRECTIVES.indexOf(this._directive) === -1) {
      throw Error(`Input directive "${this._directive}" isn't supported by CloInputText.`);
    }
  }

  ngOnInit(): void {
    if (this.value) {
      this.writeValue(this.value);
    }
  }

  public onChangeFn = (_: any) => {};

  public onTouchedFn = () => {};

  public registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  public onChange() {
    this.onChangeFn(this.value);
  }

  public onKeyUp(e) {
    let code = e.keyCode || e.which;
    if (code !== Key.Tab) {
      this.onChange();
    }
  }

  // This is a basic setter that the forms API is going to use
  public writeValue(value) {
    // if (value) {
    this.value = value;
    // }
  }


  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  constructor( private renderer: Renderer2) {

  }
}
